import { useEffect, useRef, useState } from 'react';

/* Small primitives. Deliberately hand-rolled rather than pulling a component
   library: the whole UI is a dozen shapes, and a dependency would cost more in
   bundle size and override-fighting than it saves. */

export function Button({ variant = 'secondary', size = 'md', loading, children, ...rest }) {
  return (
    <button
      className={`btn btn-${variant} btn-${size}`}
      disabled={rest.disabled || loading}
      {...rest}
    >
      {loading ? <span className="spinner-inline" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export function Badge({ tone = 'neutral', children }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

/** Maps a security/round status onto a status tone. Status colours are reserved
    and always ship with the text label, never colour alone. */
export function StatusBadge({ status }) {
  const tones = {
    OUTSTANDING: 'good',
    PARTIALLY_EXERCISED: 'info',
    EXERCISED: 'neutral',
    CANCELLED: 'neutral',
    TRANSFERRED: 'neutral',
    CONVERTED: 'info',
    EXPIRED: 'warning',
    REPURCHASED: 'neutral',
    CLOSED: 'good',
    OPEN: 'info',
    DRAFT: 'neutral',
    ACTIVE: 'good',
    TERMINATED: 'neutral',
    INACTIVE: 'neutral',
  };
  const label = String(status ?? '').replace(/_/g, ' ').toLowerCase();
  return <Badge tone={tones[status] ?? 'neutral'}>{label}</Badge>;
}

export function StatTile({ label, value, sub, tone, wide }) {
  return (
    <div className={`stat-tile${wide ? ' is-wide' : ''}`}>
      <div className="stat-label">{label}</div>
      <div className={`stat-value num${tone ? ` tone-${tone}` : ''}`}>{value}</div>
      {sub ? <div className="stat-sub">{sub}</div> : null}
    </div>
  );
}

export function Card({ title, actions, children, padded = true, className = '' }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card-head">
          {title ? <h2>{title}</h2> : <span />}
          {actions ? <div className="card-actions">{actions}</div> : null}
        </header>
      )}
      <div className={padded ? 'card-body' : 'card-body is-flush'}>{children}</div>
    </section>
  );
}

export function Spinner({ label = 'Loading' }) {
  return (
    <div className="spinner-block" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  );
}

export function ErrorBanner({ error, onRetry }) {
  if (!error) return null;
  const issues = error.issues ?? [];
  return (
    <div className="banner banner-error" role="alert">
      <div>
        <strong>{error.message}</strong>
        {error.detail ? <div className="banner-detail">{error.detail}</div> : null}
        {issues.length ? (
          <ul className="banner-issues">
            {issues.map((issue) => (
              <li key={issue.path}>
                <code>{issue.path}</code> {issue.message}
              </li>
            ))}
          </ul>
        ) : null}
        {error.requestId ? (
          <div className="banner-detail">
            Request <code>{error.requestId}</code>
          </div>
        ) : null}
      </div>
      {onRetry ? (
        <Button size="sm" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export function Empty({ title, children, action }) {
  return (
    <div className="empty-state">
      <h3>{title}</h3>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

export function Field({ label, hint, error, children, required }) {
  return (
    <label className="field">
      <span className="field-label">
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </span>
      {children}
      {hint && !error ? <span className="field-hint">{hint}</span> : null}
      {error ? <span className="field-error">{error}</span> : null}
    </label>
  );
}

export function Input(props) {
  return <input className="input" {...props} />;
}

export function Select({ children, ...rest }) {
  return (
    <select className="input" {...rest}>
      {children}
    </select>
  );
}

export function Modal({ title, onClose, children, footer, wide }) {
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Focus the first focusable control so keyboard users land inside the dialog.
    const first = ref.current?.querySelector('input, select, textarea, button');
    first?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${wide ? ' is-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <header className="modal-head">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <footer className="modal-foot">{footer}</footer> : null}
      </div>
    </div>
  );
}

/**
 * Table with a built-in "view as table" role for charts elsewhere on the page —
 * the data-viz relief rule requires the precise figures be reachable, and this
 * is where they live.
 */
export function DataTable({ columns, rows, empty, rowKey, onRowClick }) {
  if (!rows?.length) {
    return <div className="table-empty">{empty ?? 'Nothing here yet.'}</div>;
  }
  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={col.align === 'right' ? 'ta-right' : undefined}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={rowKey ? rowKey(row) : index}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={onRowClick ? 'is-clickable' : undefined}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={[col.align === 'right' ? 'ta-right' : '', col.numeric ? 'num' : '']
                    .filter(Boolean)
                    .join(' ')}
                >
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          aria-selected={active === tab.key}
          className={`tab${active === tab.key ? ' is-active' : ''}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/** Toast-ish inline confirmation, auto-dismissing. */
export function Flash({ message, tone = 'good', onDone }) {
  const [visible, setVisible] = useState(Boolean(message));
  useEffect(() => {
    setVisible(Boolean(message));
    if (!message) return undefined;
    const timer = setTimeout(() => {
      setVisible(false);
      onDone?.();
    }, 5000);
    return () => clearTimeout(timer);
  }, [message, onDone]);

  if (!visible || !message) return null;
  return (
    <div className={`banner banner-${tone}`} role="status">
      {message}
    </div>
  );
}
